import type { LegacyAudienceConfig } from '@/domain/audience/types'
import { legacyFixedUiEnglish } from '@/core/i18n/legacyFixedUi'

function directTranslation(config: LegacyAudienceConfig | null, text: string, path?: string, itemId?: string | null): string {
  const i18n = config?.i18n
  const usesActivityTranslations = i18n?.schemaVersion === 'venue-activity-i18n-v1' || i18n?.schemaVersion === 'venue-audience-i18n/v1'
  if (usesActivityTranslations) {
    if (path) {
      const exact = i18n.entries?.[`${path}::${itemId || ''}`]
      if (exact?.source === text && typeof exact.target === 'string' && exact.target.trim()) return exact.target.trim()
    }
    const targets = [...new Set(Object.values(i18n.entries || {})
      .filter((entry) => entry?.source === text && typeof entry.target === 'string' && entry.target.trim())
      .map((entry) => entry.target?.trim() || ''))]
    if (targets.length === 1) return targets[0] || ''
  }
  const snapshot = i18n?.en?.[text]
  if (typeof snapshot === 'string' && snapshot.trim()) return snapshot.trim()
  const fixed = legacyFixedUiEnglish[text]
  return typeof fixed === 'string' ? fixed.trim() : ''
}

export function translateAudienceText(config: LegacyAudienceConfig | null, value: unknown, path?: string, itemId?: string | null): string {
  const text = String(value ?? '').trim()
  if (!text) return ''
  const direct = directTranslation(config, text, path, itemId)
  if (direct) return direct

  const dynamicTicketGate = text.match(/([A-Za-z0-9]+(?:[._-][A-Za-z0-9]+)*)\s*检票口/)
  if (dynamicTicketGate) {
    const gateCode = dynamicTicketGate[1] || ''
    const templateTarget = ['{}检票口', '{gate}检票口', '{{gate}}检票口', '检票口']
      .map((placeholder) => text.replace(dynamicTicketGate[0], placeholder))
      .map((source) => source === text ? '' : translateAudienceText(config, source))
      .find(Boolean) || ''
    if (templateTarget) {
      const placeholder = /\{\{\s*gate\s*\}\}|\{\s*gate\s*\}|\{\}/i
      if (placeholder.test(templateTarget)) return templateTarget.replace(placeholder, gateCode)
      const ticketGate = /\b(?:the\s+)?(?:specific\s+)?ticket\s+gate\b/i
      if (ticketGate.test(templateTarget)) return templateTarget.replace(ticketGate, `${gateCode} Ticket Gate`)
    }
  }

  const minutes = text.match(/^约\s*(\d+(?:\.\d+)?)\s*分钟$/)
  if (minutes) return `About ${minutes[1]} ${Number(minutes[1]) === 1 ? 'minute' : 'minutes'}`

  const distance = text.match(/^(\d+(?:\.\d+)?)\s*(m|km|米|公里)$/i)
  if (distance) return `${distance[1]} ${/^(?:km|公里)$/i.test(distance[2] || '') ? 'km' : 'm'}`

  const destinationCount = text.match(/^(\d+)\s*个地点$/)
  if (destinationCount) return `${destinationCount[1]} ${Number(destinationCount[1]) === 1 ? 'destination' : 'destinations'}`

  const labelledValue = text.match(/^(服务时间|联系电话)：\s*(.+)$/)
  if (labelledValue) {
    const label = translateAudienceText(config, labelledValue[1])
    if (label) return `${label}: ${labelledValue[2]}`
  }

  const translateTokens = (tokens: string[], separator = ' ') => {
    const translated = tokens.map((token) => {
      const source = String(token ?? '').trim()
      return translateAudienceText(config, source) || (!/[\u3400-\u9fff]/.test(source) ? source : '')
    })
    return translated.every(Boolean) ? translated.join(separator) : ''
  }

  const goTo = text.match(/^前往\s*(.+)$/)
  if (goTo) {
    const translated = translateTokens(['前往', goTo[1] || ''])
    if (translated) return translated
  }

  const recommendedEntrance = text.match(/^推荐从\s*(.+?)\s*进入$/)
  if (recommendedEntrance) {
    const translated = translateTokens(['推荐从', recommendedEntrance[1] || '', '进入'])
    if (translated) return translated
  }

  const movement = text.match(/^(.+?)\s*(抵达|前往)\s*(.+)$/)
  if (movement) {
    const origin = translateAudienceText(config, movement[1])
    const destination = translateAudienceText(config, movement[3])
    if (origin && destination) return `From ${origin} to ${destination}`
  }

  const arrival = text.match(/^(抵达|到达)\s*(.+)$/)
  if (arrival) {
    const translated = translateTokens([arrival[1] || '', arrival[2] || ''])
    if (translated) return translated
  }

  for (const separator of [' + ', ' · ', ' / ']) {
    if (!text.includes(separator)) continue
    const translated = translateTokens(text.split(separator).map((part) => part.trim()), separator)
    if (translated) return translated
  }

  if (text.endsWith('路线')) {
    const subject = text.slice(0, -2).trim()
    const translatedSubject = translateAudienceText(config, subject) || (!/[\u3400-\u9fff]/.test(subject) ? subject : '')
    const translatedRoute = translateAudienceText(config, '路线')
    if (translatedSubject && translatedRoute) return `${translatedSubject.trim()} ${translatedRoute.trim()}`
  }
  if (text.endsWith('离场')) {
    const translated = translateTokens([text.slice(0, -2).trim(), '离场'])
    if (translated) return translated
  }
  return ''
}

export function localizedAudienceText(config: LegacyAudienceConfig | null, locale: 'zh-CN' | 'en', value: unknown, path?: string, itemId?: string | null): string {
  const text = String(value ?? '').trim()
  return locale === 'en' ? translateAudienceText(config, text, path, itemId) || text : text
}

export function localizeSanitizedHtml(config: LegacyAudienceConfig | null, value: string): string {
  const template = document.createElement('template')
  template.innerHTML = value
  const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT)
  const nodes: Text[] = []
  while (walker.nextNode()) nodes.push(walker.currentNode as Text)
  for (const node of nodes) {
    const raw = node.nodeValue || ''
    const source = raw.trim()
    const translated = translateAudienceText(config, source)
    if (translated) node.nodeValue = `${raw.match(/^\s*/)?.[0] || ''}${translated}${raw.match(/\s*$/)?.[0] || ''}`
  }
  template.content.querySelectorAll<HTMLElement>('[aria-label],[title],[placeholder],[alt]').forEach((element) => {
    for (const name of ['aria-label', 'title', 'placeholder', 'alt']) {
      const source = element.getAttribute(name)
      const translated = translateAudienceText(config, source)
      if (translated) element.setAttribute(name, translated)
    }
  })
  return template.innerHTML
}
